/**
 * draft-printing — Impression des bons de commande provisoires (avant paiement).
 *
 * Deux documents NON FISCAUX (voir migration 20260701120000) :
 *   - printCustomerDraft() : addition provisoire remise au client (prix + total +
 *     mention « ne vaut pas note »). Routes AIDL intégrée → BT/Network.
 *   - printKitchenDraft()  : bon de préparation cuisine (réutilise printKitchen).
 *
 * ⚠️ Ces documents ne portent AUCUN numéro/hachage fiscal. La mention provisoire
 *    est obligatoire (analogue 4.A.16) pour qu'ils ne soient jamais confondus
 *    avec un ticket de caisse NF525. Le ticket fiscal n'est émis qu'au paiement.
 *
 * Migration Phase D — invariant 4.B.2 :
 * L'appel RPC get_my_tenant_info a migré vers UsersApi.getMyTenantInfo().
 * Ce fichier n'importe plus le client Supabase directement.
 */

import { UsersApi } from '@/api/users';
import { devLog } from '@/lib/logger';
import { sendViaBluetooth } from './printer-adapters';
import { findPrinter, loadTicketConfig, printKitchen } from './printer-router';
import type { KitchenData } from './escpos-formatter';
import { normalizeDraftItems, computeDraftTotals } from './draft-ticket-format';
import { tenantAddressLines } from './tenant-address';
import type { DraftPrintItem, DraftOrderPrintInput, DraftTotals } from './draft-ticket-format';
import { printViaNetworkOrCloud } from './network-printer-adapter';

// Ré-exports pour les consommateurs existants (barrel PrintService).
export { normalizeDraftItems, computeDraftTotals };
export type { DraftPrintItem, DraftOrderPrintInput, DraftTotals };

// ---------------------------------------------------------------------------
// printCustomerDraft — addition provisoire client
// ---------------------------------------------------------------------------

/**
 * Imprime l'addition provisoire d'un bon de commande (avant paiement).
 * AIDL intégrée → BT/Network. Document NON FISCAL, mention provisoire obligatoire.
 * Le Cloud Printer (D3 Pro) n'a pas de méthode custom : ces devices impriment
 * via BT/réseau configuré.
 */
export async function printCustomerDraft(
  tenantId: string,
  draft: DraftOrderPrintInput,
): Promise<boolean> {
  const tenantResult = await UsersApi.getMyTenantInfo();
  const tenant = tenantResult.ok ? tenantResult.value : null;
  const config = await loadTicketConfig('receipt', tenantId);

  const items = normalizeDraftItems(draft.items);
  const { tvaBreakdown, totalHt, totalTva, totalTtc } = computeDraftTotals(items, draft.total);
  const ref = draft.draft_number || 'BON PROVISOIRE';
  const dateStr = new Date(draft.created_at).toLocaleString('fr-FR');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge non typé
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;

  // ── AIDL intégrée (D3/D3Mini) ──
  if (b) {
    try {
      for (let i = 0; i < 5; i++) {
        try {
          if (b.isConnected?.()) break;
        } catch {
          /* ignore */
        }
        try {
          b.init?.();
        } catch {
          /* ignore */
        }
        await new Promise((r) => setTimeout(r, 400 + i * 200));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
      const cpl = (config as any).largeur_papier === 58 ? 32 : 48;
      const ps = Math.max(
        14,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
        Math.min(22, (config as any).police_size ? (config as any).police_size - 2 : 18),
      );
      const sep = '-'.repeat(cpl);
      const dsep = '='.repeat(cpl);
      const padLine = (l: string, r: string) => {
        const maxLeft = cpl - r.length - 1;
        const trimmed = l.length > maxLeft ? l.substring(0, maxLeft) : l;
        return trimmed + ' '.repeat(Math.max(1, cpl - trimmed.length - r.length)) + r;
      };

      b.beginTransaction();
      b.setAlignment(1);
      b.setBold(true);
      b.setFontSize(ps + 4);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
      b.printText((config as any).header_title || tenant?.name || 'VisionPOS');
      b.setBold(false);
      b.setFontSize(ps);
      tenantAddressLines(tenant).forEach((line) => b.printText(line));
      b.printText(sep);
      b.setBold(true);
      b.setFontSize(ps + 4);
      b.printText('ADDITION');
      b.setBold(false);
      b.setFontSize(ps);
      b.printText('Bon : ' + ref);
      if (draft.label) b.printText(draft.label);
      b.printText(dateStr);
      b.printText(sep);
      b.setAlignment(0);
      for (const item of items) {
        const lineTotal = (item.quantity * item.unit_price).toFixed(2) + ' EUR';
        const name = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;
        b.printText(padLine(name, lineTotal));
      }
      b.printText(sep);
      if (totalTva > 0) {
        b.printText(padLine('Total HT', totalHt.toFixed(2) + ' EUR'));
        for (const t of tvaBreakdown) {
          const lbl = t.rate > 0 ? `TVA ${t.rate}% (base ${t.ht.toFixed(2)})` : 'Exonéré';
          b.printText(padLine(lbl, t.tva.toFixed(2) + ' EUR'));
        }
        b.printText(padLine('Total TVA', totalTva.toFixed(2) + ' EUR'));
      }
      b.printText(dsep);
      b.setBold(true);
      b.setFontSize(ps + 3);
      b.printText(padLine('TOTAL A PAYER', totalTtc.toFixed(2) + ' EUR'));
      b.setBold(false);
      b.setFontSize(ps);
      b.printText(dsep);
      b.setAlignment(1);
      b.setBold(true);
      b.printText('*** DOCUMENT PROVISOIRE ***');
      b.setBold(false);
      b.setFontSize(ps - 2);
      b.printText('Ne constitue pas une note de caisse.');
      b.printText('Ticket fiscal remis lors du paiement.');
      b.feedLines(3);
      b.commitTransaction();
      return true;
    } catch (err) {
      console.error('[PrintService] AIDL draft error:', err);
    }
  }

  // ── Fallback BT/Network ──
  const printer = await findPrinter(tenantId, draft.register_id || undefined, 'receipt');
  if (!printer) {
    devLog('[printCustomerDraft] Aucune imprimante disponible pour addition provisoire');
    return false;
  }
  const cpl = printer.char_per_line || 48;
  const sep = '-'.repeat(cpl);
  const dsep = '='.repeat(cpl);
  const padLine = (l: string, r: string) => {
    const maxLeft = cpl - r.length - 1;
    const trimmed = l.length > maxLeft ? l.substring(0, maxLeft) : l;
    return trimmed + ' '.repeat(Math.max(1, cpl - trimmed.length - r.length)) + r;
  };
  const ESC_INIT = '\x1B@';
  const ALIGN_C = '\x1B\x61\x01';
  const ALIGN_L = '\x1B\x61\x00';
  const BOLD_ON = '\x1B\x45\x01';
  const BOLD_OFF = '\x1B\x45\x00';
  const SIZE_L = '\x1D\x21\x10';
  const SIZE_N = '\x1D\x21\x00';
  const CUT = '\x1D\x56\x01';

  let t =
    ESC_INIT +
    ALIGN_C +
    BOLD_ON +
    SIZE_L +
    (tenant?.name || 'VisionPOS') +
    '\n' +
    SIZE_N +
    BOLD_OFF;
  tenantAddressLines(tenant).forEach((line) => {
    t += line + '\n';
  });
  t += sep + '\n' + BOLD_ON + SIZE_L + 'ADDITION\n' + SIZE_N + BOLD_OFF;
  t += 'Bon : ' + ref + '\n';
  if (draft.label) t += draft.label + '\n';
  t += dateStr + '\n' + sep + '\n' + ALIGN_L;
  for (const item of items) {
    const lineTotal = (item.quantity * item.unit_price).toFixed(2) + ' EUR';
    const name = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;
    t += padLine(name, lineTotal) + '\n';
  }
  t += sep + '\n';
  if (totalTva > 0) {
    t += padLine('Total HT', totalHt.toFixed(2) + ' EUR') + '\n';
    for (const tv of tvaBreakdown) {
      const lbl = tv.rate > 0 ? `TVA ${tv.rate}% (base ${tv.ht.toFixed(2)})` : 'Exonéré';
      t += padLine(lbl, tv.tva.toFixed(2) + ' EUR') + '\n';
    }
    t += padLine('Total TVA', totalTva.toFixed(2) + ' EUR') + '\n';
  }
  t +=
    dsep +
    '\n' +
    BOLD_ON +
    SIZE_L +
    padLine('TOTAL A PAYER', totalTtc.toFixed(2) + ' EUR') +
    '\n' +
    SIZE_N +
    BOLD_OFF +
    dsep +
    '\n' +
    ALIGN_C +
    BOLD_ON +
    '*** DOCUMENT PROVISOIRE ***\n' +
    BOLD_OFF +
    'Ne constitue pas une note de caisse.\n' +
    'Ticket fiscal remis lors du paiement.\n\n\n\n' +
    CUT;

  const bytes = new TextEncoder().encode(t);
  try {
    if (printer.connection_type === 'bluetooth' && printer.bt_mac_address) {
      return await sendViaBluetooth(printer.bt_mac_address, bytes);
    }
    if (printer.connection_type === 'network' && printer.ip_address) {
      return await printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, bytes);
    }
  } catch (err) {
    console.error('[PrintService] BT/Network draft error:', err);
  }
  return false;
}

// ---------------------------------------------------------------------------
// printKitchenDraft — bon de préparation cuisine (réutilise printKitchen)
// ---------------------------------------------------------------------------

/**
 * Imprime le bon de préparation cuisine d'un bon de commande.
 * Réutilise le pipeline cuisine existant (printKitchen) — pas de prix.
 */
export async function printKitchenDraft(
  tenantId: string,
  draft: DraftOrderPrintInput,
  destination: 'CUISINE' | 'BAR' = 'CUISINE',
): Promise<boolean> {
  const items = normalizeDraftItems(draft.items).map((it) => ({
    name: it.name,
    quantity: it.quantity,
  }));
  const data: KitchenData = {
    destination,
    order_number: draft.draft_number || 'BON',
    server_name: draft.label || 'Comptoir',
    table_number: draft.label || undefined,
    items,
  };
  return printKitchen(tenantId, draft.register_id || undefined, data);
}
